import { useCallback } from 'react';

import {
  getHours,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfHour
} from 'date-fns';

import { MAX_HOUR, MIN_HOUR } from '../config/calendarConfig';

interface UseSlotValidationProps {
  maxSelectableDate: Date;
}

export const useSlotValidation = ({
  maxSelectableDate
}: UseSlotValidationProps) => {
  const isSlotDisabled = useCallback(
    (date: Date) => {
      const now = new Date();
      const slotStart = startOfHour(date);
      const hour = getHours(date);

      // Check if the time is within the allowed range
      if (hour < MIN_HOUR || hour >= MAX_HOUR) {
        return true;
      }

      // If the date is in the past (not today) - block
      if (isBefore(startOfDay(date), startOfDay(now))) {
        return true;
      }

      // If the date is after the maximum available - block
      if (isAfter(startOfDay(date), maxSelectableDate)) {
        return true;
      }

      // If today's day
      if (isSameDay(date, now)) {
        // Block if the hour has already started or passed
        return !isAfter(slotStart, startOfHour(now));
      }

      return false;
    },
    [maxSelectableDate]
  );

  const dayPropGetter = useCallback(
    (date: Date) => {
      const now = new Date();
      const dayStart = startOfDay(date);

      // Gray out dates that are in the past or after the max selectable date
      if (
        isBefore(dayStart, startOfDay(now)) ||
        isAfter(dayStart, maxSelectableDate)
      ) {
        return {
          className: 'rbc-slot-disabled'
        };
      }
      return {};
    },
    [maxSelectableDate]
  );

  const slotPropGetter = useCallback(
    (date: Date) => {
      if (isSlotDisabled(date)) {
        return {
          className: 'rbc-slot-disabled'
        };
      }
      return {};
    },
    [isSlotDisabled]
  );

  return {
    isSlotDisabled,
    dayPropGetter,
    slotPropGetter
  };
};
